// src/hooks/useApi.js
import { useState, useEffect, useCallback } from 'react';
import API from '@/api/api';

/**
 * 범용 데이터 Fetching을 위한 커스텀 훅
 * @param {string|null} url - API 요청 URL. null일 경우 요청을 보내지 않음.
 * @param {object} options - Axios 요청에 전달할 옵션 (params, body 등)
 * @returns {{data: any, loading: boolean, error: any, refetch: function}}
 */
export function useApi(url, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(
    async (abortSignal) => {
      // URL이 null이면 요청을 스킵
      if (!url) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await API.get(url, { ...options, signal: abortSignal });
        setData(response.data);
      } catch (err) {
        if (err.name !== 'CanceledError') {
          setError(err);
          console.error(`API Error fetching ${url}:`, err);
        }
      } finally {
        setLoading(false);
      }
    },
    [url, JSON.stringify(options)]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);

    // 클린업 함수: 컴포넌트 언마운트 시 요청 취소
    return () => {
      controller.abort();
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
